import { AgentThreadVoStatus } from "@/api";
import { AgentThreadMessage, chatStore } from "@/store/chat";
import { threadStore } from "@/store/thread";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

import { observer } from "mobx-react-lite";

interface ThreadMessageContextType {
  reconnect: () => void;
}

const ThreadMessageContext = createContext<ThreadMessageContextType | null>(
  null
);

interface ThreadMessageProviderProps {
  threadId: string;
  children: ReactNode;
}

export const ThreadMessageProvider = observer(
  ({ threadId, children }: ThreadMessageProviderProps) => {
    const eventSourceRef = useRef<EventSource | null>(null);
    const isManualCloseRef = useRef(false);
    const retryCountRef = useRef(0);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const threadType = threadStore.thread?.type;

    const connect = useCallback(() => {
      if (!threadId) return;

      // Only connect for streaming threads
      if (threadType !== "streaming") {
        return;
      }

      // Close existing connection if any
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      isManualCloseRef.current = false;
      const url = `/api/v1/threads/${threadId}/messages/sse`;
      const eventSource = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          // Reset retry count on successful message
          retryCountRef.current = 0;

          const data = JSON.parse(event.data) as {
            messages: AgentThreadMessage[];
            thread_status: AgentThreadVoStatus;
          };

          // Handle messages update
          if (data.messages) {
            chatStore.setStreamingMessages(
              threadId,
              data.thread_status,
              data.messages as AgentThreadMessage[]
            );
          }

          // Handle thread status update
          if (data.thread_status) {
            const currentThread = threadStore.thread;
            if (currentThread) {
              threadStore.set("thread", {
                ...currentThread,
                status: data.thread_status,
              });
            }
          }
        } catch (error) {
          console.error("Failed to parse SSE message:", error);
        }
      };

      eventSource.onerror = (error) => {
        console.error("SSE connection error:", error, eventSource.readyState);

        eventSource.close();
        eventSourceRef.current = null;

        // Only retry if thread is not hung up (not in Success/Failed/Terminated state)
        // and not manually closed
        if (!threadStore.isThreadHangUp && !isManualCloseRef.current) {
          const maxRetries = 5;
          if (retryCountRef.current < maxRetries) {
            // Exponential backoff: 1s, 2s, 4s, 8s, 16s
            const delay = Math.min(
              1000 * Math.pow(2, retryCountRef.current),
              16000
            );
            console.log(
              `Attempting to reconnect in ${delay}ms (attempt ${
                retryCountRef.current + 1
              }/${maxRetries})...`
            );

            retryTimeoutRef.current = setTimeout(() => {
              retryCountRef.current++;
              connect();
            }, delay);
          } else {
            console.error(
              "Max retry attempts reached, giving up reconnection."
            );
          }
        }
      };
    }, [threadId, threadType]);

    const reconnect = useCallback(() => {
      // Reset retry count on manual reconnect
      retryCountRef.current = 0;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      connect();
    }, [connect]);

    useEffect(() => {
      connect();

      return () => {
        isManualCloseRef.current = true;
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }
      };
    }, [connect]);

    return (
      <ThreadMessageContext.Provider value={{ reconnect }}>
        {children}
      </ThreadMessageContext.Provider>
    );
  }
);

export function useThreadMessage() {
  const context = useContext(ThreadMessageContext);
  if (!context) {
    throw new Error(
      "useThreadMessage must be used within a ThreadMessageProvider"
    );
  }
  return context;
}
